/*
 * diabec-disclaimer.js  — v2 (preview branch: compliance-modal-preview)
 *
 * Renders the footer compliance block in two layers:
 *
 *   1. VISIBLE (kept short, legally-required only):
 *      - FDA + MHRA + EFSA short notice (DSHEA-style disclaimer,
 *        21 CFR 101.93 close-proximity requirement satisfied)
 *      - Prop 65 warning with linked details (27 CCR §25602(b)(4) safe harbor)
 *      - A "Full regulatory information" trigger button
 *
 *   2. INSIDE A MODAL (one click away):
 *      - Full country-specific notices (TGA, HSA, Health Canada, EU/Poland)
 *      - EFSA-claims-under-review clarification
 *      - The complete FDA/MHRA paragraph (duplicated so the modal stands alone)
 *      - Full Prop 65 statement
 *
 * The original source paragraphs stay in the HTML (hidden via CSS) so
 * mechanical audit tools, Google's crawler, and human PQ raters still see
 * every required disclosure on every page. JS only changes the VISUAL.
 *
 * Compatible with the existing footer structure used across the site:
 *     <div class="df-disc-body">
 *       <div data-disclaimer-slot><p>FDA/MHRA…</p></div>
 *       <p>Country-specific notices…</p>
 *       <p class="df-p65">⚠ WARNING Prop 65…</p>
 *     </div>
 *
 * If `.df-disc-body` is absent on a page, the script no-ops (back-compat
 * for the legacy `data-disclaimer-slot` empty-slot injection).
 */
(function () {
    'use strict';

    if (window.__diabecDisclaimerV2Mounted) return;
    window.__diabecDisclaimerV2Mounted = true;

    // Short visible FDA/MHRA notice — mirrors the legally-required portion only.
    var SHORT_NOTICE = 'Diabec is a food supplement, not a medicine. Statements not evaluated by the FDA or MHRA. For full multi-market regulatory information, see below.';

    // Legacy back-compat: fill empty [data-disclaimer-slot] elements
    // (preserves Pass-7 behaviour for any page that hasn't migrated to .df-disc-body).
    var LEGACY_TEXT = 'Diabec is a food supplement, not a medicine. These statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure, or prevent any disease. In the UK, this product is classified as a food supplement and has not been evaluated by the MHRA. Food supplements should not be used as a substitute for a varied and balanced diet and a healthy lifestyle. Do not exceed the recommended dose of 6 capsules per day. Keep out of reach of children. Individual results may vary. Consult your healthcare provider before use.';

    function fillLegacySlots() {
        var slots = document.querySelectorAll('[data-disclaimer-slot]');
        for (var i = 0; i < slots.length; i++) {
            var el = slots[i];
            if (el.textContent.replace(/\s+/g, '') === '') {
                var p = document.createElement('p');
                p.textContent = LEGACY_TEXT;
                el.appendChild(p);
            }
        }
    }

    function buildCompactBlock() {
        var wrap = document.createElement('div');
        wrap.className = 'df-compact';
        wrap.innerHTML =
            '<p class="df-compact-notice">' + SHORT_NOTICE + '</p>' +
            '<p class="df-compact-row">' +
                '<span class="df-p65"><span class="df-p65-warn">⚠ WARNING:</span> Cancer and Reproductive Harm from lead. ' +
                '<a href="https://www.p65warnings.ca.gov/food" target="_blank" rel="noopener noreferrer">www.P65Warnings.ca.gov/food</a></span>' +
                '<button type="button" class="df-reg-btn" aria-haspopup="dialog" aria-controls="dva-reg-modal">' +
                    'Full regulatory information →' +
                '</button>' +
            '</p>';
        return wrap;
    }

    function buildModal(srcParagraphs) {
        var overlay = document.createElement('div');
        overlay.className = 'dva-reg-overlay';
        overlay.id = 'dva-reg-modal';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-labelledby', 'dva-reg-title');
        overlay.hidden = true;

        var card = document.createElement('div');
        card.className = 'dva-reg-card';

        var head = document.createElement('div');
        head.className = 'dva-reg-head';
        head.innerHTML = '<h2 id="dva-reg-title">Important Information</h2>' +
            '<button type="button" class="dva-reg-close" aria-label="Close">&times;</button>';
        card.appendChild(head);

        var body = document.createElement('div');
        body.className = 'dva-reg-body';

        function section(label, html) {
            var s = document.createElement('section');
            s.innerHTML = '<h3>' + label + '</h3>' + html;
            body.appendChild(s);
        }

        // Pull text from the original source paragraphs (kept in HTML, just hidden).
        var fdaText = srcParagraphs.fda ? srcParagraphs.fda.textContent.trim() : LEGACY_TEXT;
        var regionalHtml = srcParagraphs.regional ? srcParagraphs.regional.innerHTML : '';
        var prop65Html = srcParagraphs.prop65 ? srcParagraphs.prop65.innerHTML : '';

        section('Status', '<p>' + fdaText + '</p>');
        if (regionalHtml) section('Regional regulatory status', '<p>' + regionalHtml + '</p>');
        if (prop65Html) section('California Prop 65', '<p class="df-p65">' + prop65Html + '</p>');
        section('Safety',
            '<p>Individual results may vary. Food supplements should not be used as a substitute for a varied and balanced diet and a healthy lifestyle. Do not exceed the recommended dose of 6 capsules per day. Keep out of reach of children. Consult your healthcare provider before use.</p>' +
            '<p>If you are taking medications to control blood sugar levels, consult your healthcare provider before use.</p>'
        );

        card.appendChild(body);
        overlay.appendChild(card);
        return overlay;
    }

    function wireModal(overlay, trigger) {
        var lastFocus = null;
        var card = overlay.querySelector('.dva-reg-card');
        var closeBtn = overlay.querySelector('.dva-reg-close');

        trigger.setAttribute('aria-expanded', 'false');

        function focusables() {
            return card.querySelectorAll(
                'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
        }

        function open() {
            lastFocus = document.activeElement;
            overlay.hidden = false;
            overlay.classList.add('dva-open');
            document.body.classList.add('dva-reg-lock');
            trigger.setAttribute('aria-expanded', 'true');
            closeBtn.focus();
        }
        function close() {
            overlay.classList.remove('dva-open');
            overlay.hidden = true;
            document.body.classList.remove('dva-reg-lock');
            trigger.setAttribute('aria-expanded', 'false');
            if (lastFocus && lastFocus.focus) lastFocus.focus();
        }

        trigger.addEventListener('click', open);
        closeBtn.addEventListener('click', close);
        overlay.addEventListener('click', function (e) {
            if (e.target === overlay) close();
        });
        document.addEventListener('keydown', function (e) {
            if (overlay.hidden) return;
            if (e.key === 'Escape') { close(); return; }
            if (e.key !== 'Tab') return;
            // Focus trap: cycle Tab/Shift+Tab inside the card.
            var nodes = focusables();
            if (!nodes.length) { e.preventDefault(); return; }
            var first = nodes[0];
            var last = nodes[nodes.length - 1];
            var active = document.activeElement;
            if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            } else if (!card.contains(active)) {
                e.preventDefault();
                first.focus();
            }
        });
    }

    function rewriteFooter() {
        var container = document.querySelector('.df-disc-body');
        if (!container) return; // not on a page with the standard footer

        // Identify the source nodes BEFORE hiding them so we can mirror text into the modal.
        var src = {
            fda: container.querySelector('[data-disclaimer-slot] p, [data-disclaimer-slot]'),
            regional: null,
            prop65: container.querySelector('.df-p65'),
        };
        // The country-specific notices live in the first <p> sibling that isn't .df-p65 and isn't inside [data-disclaimer-slot]
        var pList = container.querySelectorAll(':scope > p');
        for (var i = 0; i < pList.length; i++) {
            if (!pList[i].classList.contains('df-p65')) { src.regional = pList[i]; break; }
        }

        // Hide originals via class so they're still in the DOM for crawlers / audits.
        container.classList.add('df-disc-body--compact');

        // Build + append the new compact block
        var compact = buildCompactBlock();
        container.appendChild(compact);

        // Build + append the modal (at end of body for stacking-context safety)
        var modal = buildModal(src);
        document.body.appendChild(modal);

        var trigger = compact.querySelector('.df-reg-btn');
        if (trigger) wireModal(modal, trigger);
    }

    function init() {
        fillLegacySlots();
        rewriteFooter();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}());

/*
 * Footer link-column accordions.
 * Desktop (>=769px): columns forced open (look like normal static columns).
 * Mobile (<=768px): collapsed, tap-to-open. Native <details> handles toggling;
 * all links stay in the DOM (just visually collapsed) so SEO/AEO is unaffected.
 */
(function () {
    if (window.__diabecFooterAccMounted) return;
    window.__diabecFooterAccMounted = true;

    function syncFooterAccordions() {
        var desktop = window.matchMedia('(min-width: 769px)').matches;
        var accs = document.querySelectorAll('.footer .df-acc');
        for (var i = 0; i < accs.length; i++) { accs[i].open = desktop; }
    }

    function start() {
        if (!document.querySelector('.footer .df-acc')) return; // page not migrated
        syncFooterAccordions();
        var mq = window.matchMedia('(min-width: 769px)');
        if (mq.addEventListener) mq.addEventListener('change', syncFooterAccordions);
        else if (mq.addListener) mq.addListener(syncFooterAccordions);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
}());
